import { useState, useEffect, useRef, useCallback } from 'react';
import Header from './components/Header';
import ChatInterface from './components/ChatInterface';
import QuickReplyButtons from './components/QuickReplyButtons';
import MessageInput from './components/MessageInput';
import PremiumModal from './components/PremiumModal';
import FarewellAnimation from './components/FarewellAnimation';
import Footer from './components/Footer';
import SetupScreen from './components/SetupScreen';
import TrialStatusBar from './components/TrialStatusBar';
import IntimacyBar from './components/IntimacyBar';
import VoiceButton from './components/VoiceButton';
import { MemoryConfirmation } from './components/MemoryConfirmation';
import { useChatHistory } from '../../hooks/useChatHistory';
import { useMemoryDB } from '../../hooks/useMemoryDB';
import { useMemoryExtraction } from '../../hooks/useMemoryExtraction';
import { useMemoryWeaving } from '../../hooks/useMemoryWeaving';
import { useReminder } from '../../hooks/useReminder';
import { useTrialSystem } from '../../hooks/useTrialSystem';
import { useDailyLoop } from '../../hooks/useDailyLoop';
import { useRevisitGreeting } from '../../hooks/useRevisitGreeting';
import { useIntimacy } from '../../hooks/useIntimacy';
import { usePersonalization } from '../../hooks/usePersonalization';
import { getQuoteForUserMessage } from '../../mocks/quotes';
import { useSupabasePersona } from '../../hooks/useSupabasePersona';
import { useSupabaseProfile } from '../../hooks/useSupabaseProfile';
import { useSupabasePreferences } from '../../hooks/useSupabasePreferences';
import { useSupabaseRelationMemory } from '../../hooks/useSupabaseRelationMemory';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';
import { getChatCompletionV2, getErrorMessage, type ThreeTierPersonaData } from '../../services/openaiClient';
import type { MemorySuggestion } from '../../hooks/useMemoryExtraction';
import type { IntimacyLevel } from '../../hooks/useIntimacy';
import JsonLd from '../../components/seo/JsonLd';
import SEOHead from '../../components/seo/SEOHead';

interface Message {
  type: 'ai' | 'user' | 'system';
  text: string;
  time?: string;
  memorySuggestion?: MemorySuggestion;
}

function getAizuchi(userText: string): string {
  const empathyWords = ['大変', 'つらい', '疲れた', 'しんどい', '辛い', '悩み', '失敗', 'できない', '無理', '自分が嫌い', 'きつい'];
  const happyWords = ['嬉しい', '楽しい', '良かった', '最高', '幸せ', '好き', '可愛い', 'かわいい', '成功', 'うまくいった'];
  const greetings = ['おはよう', 'こんにちは', 'こんばんは', 'ただいま'];
  const surpriseWords = ['びっくり', '驚いた', '信じられない', 'まじで', 'え、', 'えっ'];
  const sadWords = ['悲しい', '泣いた', '泣きそう', '寂しい', 'さみしい', '切ない'];
  const angryWords = ['むかつく', 'イライラ', '腹立つ', '怒った', 'ひどい', '最悪'];
  const bigDecisionWords = ['退職', '独立', '転職', '引っ越し', '別れ', '結婚', '起業', '辞め'];

  if (bigDecisionWords.some((w) => userText.includes(w))) {
    const list = ['そうなんですね。', 'そうだったんですね。', 'なるほど…'];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (sadWords.some((w) => userText.includes(w))) {
    const list = ['そっか…🥺', 'それは悲しいですね…😢', 'うん…聞いてますよ。', 'そうだったんですね…💕'];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (angryWords.some((w) => userText.includes(w))) {
    const list = ['それはひどいですね…😢', 'うん、わかります。', 'それは腹立ちますよね😤'];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (empathyWords.some((w) => userText.includes(w))) {
    const list = ['そっか…🥺', 'うん、うん…', 'お疲れさまです😊', 'そうだったんですね…', 'ちゃんと聞いてます💕'];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (happyWords.some((w) => userText.includes(w))) {
    const list = ['わあ✨', 'それは嬉しいですね😊', 'いいですね！🌸', 'よかったです〜！😊'];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (surpriseWords.some((w) => userText.includes(w))) {
    const list = ['え、ほんとですか？😮', 'それはびっくりですね！', 'まじですか？！😲'];
    return list[Math.floor(Math.random() * list.length)];
  }
  if (greetings.some((w) => userText.includes(w))) {
    const list = ['あ、来てくれました🌸', 'おかえりなさい！😊', 'こんにちは♪✨'];
    return list[Math.floor(Math.random() * list.length)];
  }
  const defaults = ['そうなんですね。', 'なるほど…', 'そっか。', 'うん、うん。'];
  return defaults[Math.floor(Math.random() * defaults.length)];
}

function getEmotionEmoji(emotion: 'happy' | 'sad' | 'tired' | 'anxious' | 'neutral' | 'love' | 'shy' | 'excited' | 'angry' | 'relief' | 'surprise'): string {
  const emojiMap = {
    happy:   ['😊', '🌸', '✨', '🎵', '😄', '🌟', '💫', '🌈'],
    sad:     ['😢', '🥺', '💧', '😔', '🌧️', '💦'],
    tired:   ['😌', '💤', '🌙', '😮‍💨', '🛌', '☕'],
    anxious: ['😰', '💦', '😥', '🫤', '😟', '💭'],
    neutral: ['😊', '🌸', '💕', '🍀', '☁️'],
    love:    ['❤️', '💕', '💖', '💗', '🥰', '💓', '💞'],
    shy:     ['😳', '☺️', '🙈', '💗', '🫣'],
    excited: ['✨', '🎉', '😆', '🌟', '🎊', '🔥', '💥'],
    angry:   ['😤', '💢', '😠', '🌩️', '😡'],
    relief:  ['😮‍💨', '🍃', '😌', '🌤️', '✨'],
    surprise:['😮', '😲', '🫢', '‼️', '👀'],
  };
  const list = emojiMap[emotion];
  return list[Math.floor(Math.random() * list.length)];
}

function getEmojiFrequency(intimacyLevel: IntimacyLevel): number {
  return 0.1 + (intimacyLevel * 0.1);
}

function addEmojisToMessage(message: string, userText: string, intimacyLevel: IntimacyLevel): string {
  const frequency = getEmojiFrequency(intimacyLevel);
  const existingEmojiCount = (message.match(/[\u{1F300}-\u{1F9FF}]/gu) || []).length;
  if (existingEmojiCount >= 2) return message;
  if (Math.random() > frequency) return message;

  const sadWords = ['悲しい', '泣いた', '泣きそう', '寂しい', 'さみしい', '切ない'];
  const happyWords = ['嬉しい', '楽しい', '良かった', '最高', '幸せ'];
  const tiredWords = ['疲れた', 'しんどい', 'つらい', '大変', 'きつい'];
  const anxiousWords = ['不安', '心配', '怖い', '緊張'];
  const loveWords = ['好き', '大好き', '愛してる', '可愛い', 'かわいい'];
  const shyWords = ['照れる', '恥ずかしい', 'ドキドキ'];
  const angryWords = ['むかつく', 'イライラ', '腹立つ', '怒った', 'ひどい', '最悪'];
  const reliefWords = ['よかった', 'ほっとした', '安心', '解決'];
  const surpriseWords = ['びっくり', '驚いた', '信じられない', 'まじで', 'え、'];

  let emotion: 'happy' | 'sad' | 'tired' | 'anxious' | 'neutral' | 'love' | 'shy' | 'excited' | 'angry' | 'relief' | 'surprise' = 'neutral';
  if (sadWords.some((w) => userText.includes(w))) emotion = 'sad';
  else if (happyWords.some((w) => userText.includes(w))) emotion = 'happy';
  else if (tiredWords.some((w) => userText.includes(w))) emotion = 'tired';
  else if (anxiousWords.some((w) => userText.includes(w))) emotion = 'anxious';
  else if (loveWords.some((w) => userText.includes(w))) emotion = 'love';
  else if (shyWords.some((w) => userText.includes(w))) emotion = 'shy';
  else if (angryWords.some((w) => userText.includes(w))) emotion = 'angry';
  else if (reliefWords.some((w) => userText.includes(w))) emotion = 'relief';
  else if (surpriseWords.some((w) => userText.includes(w))) emotion = 'surprise';
  else if (userText.includes('すごい') || userText.includes('やった')) emotion = 'excited';

  const emoji = getEmotionEmoji(emotion);
  const sentences = message.split('\n');
  if (sentences.length > 0) {
    const lastSentence = sentences[sentences.length - 1];
    if (/[\u{1F300}-\u{1F9FF}]$/u.test(lastSentence)) return message;
    sentences[sentences.length - 1] = lastSentence + emoji;
  }
  return sentences.join('\n');
}

function getHobbyBasedReply(userText: string, _name: string): string | null {
  if (userText.includes('カフェ') || userText.includes('コーヒー') || userText.includes('お茶')) {
    const replies = [
      `私もカフェ巡りが好きなんです☕\nゆっくりできる場所、いいですよね。`,
      `カフェでゆっくりする時間、大切ですよね😊\n私も好きです。`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  if (userText.includes('散歩') || userText.includes('夜') || userText.includes('夜中')) {
    const replies = [
      `私も夜の散歩が好きなんです🌙\n静かで落ち着きますよね。`,
      `夜の時間、いいですよね。\n私も夜の雑談が好きです✨`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  if (userText.includes('聞いて') || userText.includes('話を聞いて') || userText.includes('相談')) {
    const replies = [
      `人の話を聞くの、好きなんです😊\nゆっくり話してください。`,
      `話を聞くのが好きなので、\nいくらでも聞きますよ💕`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  return null;
}

function getWeaknessBasedReply(userText: string, name: string, messageCount: number): string | null {
  if (messageCount > 15 && Math.random() < 0.15) {
    const replies = [
      `${name}と話してると、\n時間があっという間です🌸`,
      `もう少し話していたいな…💕`,
      `また来てくれますよね？\nちょっと寂しくなっちゃいます😊`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  if (userText.includes('無理') || userText.includes('頑張') || userText.includes('大丈夫')) {
    const replies = [
      `無理してません？\nちょっと心配です🥺`,
      `頑張りすぎないですか？\n少し休んでもいいと思います😊`,
      `${name}、無理しないでくださいね。\n心配になっちゃいます💕`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  if (userText.includes('自分が') || userText.includes('ダメ') || userText.includes('できない')) {
    const replies = [
      `そんなに自分を責めないでください🥺\n見てて心配になります。`,
      `${name}は十分頑張ってますよ。\nもっと自分を褒めてあげてください✨`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  return null;
}

function getPositiveUserReaction(userText: string, name: string): string | null {
  if (userText.includes('頑張') || userText.includes('努力') || userText.includes('やってみ')) {
    const replies = [
      `${name}、ちゃんと頑張ってるの知ってますよ✨\nそういうところ、好きです。`,
      `頑張ってる人、好きなんです😊\n${name}みたいな人。`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  if (userText.includes('ありがとう') || userText.includes('心配') || userText.includes('大丈夫')) {
    const replies = [
      `${name}、優しいですね😊\nそういうところ、好きです。`,
      `優しい人、好きなんです。\n${name}みたいな人💕`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  if (userText.includes('仕事') || userText.includes('勉強') || userText.includes('ちゃんと')) {
    const replies = [
      `真面目な人、好きなんです😊\n${name}みたいな人。`,
      `ちゃんとしてる人、素敵だと思います✨`,
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  }
  return null;
}

function getSilencePattern(): string | null {
  if (Math.random() < 0.05) {
    const patterns = [
      'うーん…',
      'そうですね…',
      'ちょっと考えちゃいました。',
      '少し考えてました😊',
      'なんか、うまく言えないけど…',
      'そっか…',
    ];
    return patterns[Math.floor(Math.random() * patterns.length)];
  }
  return null;
}

function inferEmotion(userText: string): {
  emotion: 'anxious' | 'relief' | 'tired' | 'happy' | 'sad' | 'angry' | 'excited' | 'neutral';
  intensity: 'low' | 'medium' | 'high';
} {
  const bigDecisionWords = ['退職', '独立', '転職', '引っ越し', '別れ', '結婚', '起業', '辞め'];
  const tiredWords = ['疲れた', 'しんどい', 'つらい', '大変', 'きつい', '辛い', 'ヘトヘト'];
  const happyWords = ['嬉しい', '楽しい', '良かった', '最高', '幸せ', '成功', 'うまくいった'];
  const sadWords = ['悲しい', '泣いた', '泣きそう', '寂しい', 'さみしい', '切ない'];
  const angryWords = ['むかつく', 'イライラ', '腹立つ', '怒った', 'ひどい', '最悪'];
  const anxiousWords = ['不安', '心配', '怖い', '緊張', 'ドキドキ', 'どうしよう'];
  const excitedWords = ['ワクワク', 'ドキドキ', '楽しみ', '期待', 'すごい', 'やった'];

  if (bigDecisionWords.some((w) => userText.includes(w))) {
    if (userText.includes('バタバタ') || userText.includes('忙しい') || userText.includes('大変')) {
      return { emotion: 'anxious', intensity: 'high' };
    }
    return { emotion: 'anxious', intensity: 'medium' };
  }
  if (sadWords.some((w) => userText.includes(w))) return { emotion: 'sad', intensity: 'high' };
  if (angryWords.some((w) => userText.includes(w))) return { emotion: 'angry', intensity: 'high' };
  if (tiredWords.some((w) => userText.includes(w))) return { emotion: 'tired', intensity: 'medium' };
  if (anxiousWords.some((w) => userText.includes(w))) return { emotion: 'anxious', intensity: 'medium' };
  if (excitedWords.some((w) => userText.includes(w))) return { emotion: 'excited', intensity: 'medium' };
  if (happyWords.some((w) => userText.includes(w))) return { emotion: 'happy', intensity: 'medium' };
  return { emotion: 'neutral', intensity: 'low' };
}

function generateEmpathy(userText: string, emotion: ReturnType<typeof inferEmotion>): string {
  const bigDecisionWords = ['退職', '独立', '転職', '引っ越し', '別れ', '結婚', '起業', '辞め'];
  const longTermWords = ['長年', '長く', 'ずっと', '何年も'];

  if (bigDecisionWords.some((w) => userText.includes(w))) {
    if (longTermWords.some((w) => userText.includes(w))) {
      const list = [
        '長く働いていた会社を離れるのって、いろんな気持ちがありますよね。',
        '長くいた場所を離れるのって、結構勇気いりますよね。',
      ];
      return list[Math.floor(Math.random() * list.length)];
    }
    const list = ['独立って大きな決断だと思います。', 'それは大きな一歩ですね。', '勇気いりましたよね。'];
    return list[Math.floor(Math.random() * list.length)];
  }

  switch (emotion.emotion) {
    case 'anxious': return ['少し不安もありますよね。', '大変でしたね。', 'いろいろ考えますよね。'][Math.floor(Math.random() * 3)];
    case 'tired': return ['それは大変でしたね。', 'お疲れさまです。', 'しんどかったですね。'][Math.floor(Math.random() * 3)];
    case 'sad': return ['それは悲しいですね…', 'つらかったですね。', 'そっか…大変でしたね。'][Math.floor(Math.random() * 3)];
    case 'angry': return ['それはひどいですね。', '腹立ちますよね。', 'よく我慢しましたね。'][Math.floor(Math.random() * 3)];
    case 'happy': return ['それは嬉しいですね！', 'よかったですね😊', 'すごいです！'][Math.floor(Math.random() * 3)];
    case 'excited': return ['ワクワクしますね！', '楽しみですね✨', 'いいですね！'][Math.floor(Math.random() * 3)];
    default: return 'ちゃんと頑張ってますね。';
  }
}

function generateFollowUpQuestion(userText: string, emotion: ReturnType<typeof inferEmotion>): string {
  const bigDecisionWords = ['退職', '独立', '転職', '引っ越し', '別れ', '結婚', '起業', '辞め'];

  if (bigDecisionWords.some((w) => userText.includes(w))) {
    if (userText.includes('バタバタ') || userText.includes('忙しい')) {
      const questions = [
        '最初は大変ですよね。\nでも新しいスタートってワクワクもありますよね。',
        '忙しい中でも、少し楽しみな気持ちもあります？',
        'バタバタしてる中でも、ちゃんと前に進んでるんですね。',
        '落ち着いたら、ゆっくり話してくださいね。',
      ];
      return questions[Math.floor(Math.random() * questions.length)];
    }
    const questions = [
      '独立してみて、今はどんな感じですか？',
      'どんなきっかけだったんですか？',
      '一番大変だったのはどこですか？',
      '今の気持ち、どうですか？',
      'そこに至るまで、いろいろあったんですか？',
    ];
    return questions[Math.floor(Math.random() * questions.length)];
  }

  switch (emotion.emotion) {
    case 'anxious':
      return [
        '今はどんな気持ちですか？',
        '少し落ち着きました？',
        '一番心配なのはどこですか？',
        '誰かに話せてますか？',
        '今、何が一番しんどいですか？',
        'ゆっくり話してくれていいですよ。',
      ][Math.floor(Math.random() * 6)];
    case 'tired':
      return [
        '今日は休めそうですか？',
        '少し休めました？',
        '今はどんな感じですか？',
        '何が一番疲れましたか？',
        '今夜はゆっくりできそうですか？',
        '体、大丈夫ですか？',
      ][Math.floor(Math.random() * 6)];
    case 'sad':
      return [
        '今はどうですか？',
        '少し落ち着きました？',
        '話したいことあります？',
        '何があったか、話せますか？',
        'ゆっくりでいいですよ。',
        '今、そばにいますよ。',
      ][Math.floor(Math.random() * 6)];
    case 'angry':
      return [
        'その後どうなりましたか？',
        '少し落ち着きました？',
        '誰かに話せましたか？',
        'まだモヤモヤしてますか？',
        'どうしたかったんですか？',
      ][Math.floor(Math.random() * 5)];
    case 'happy':
      return [
        'どんなことがあったんですか？',
        'もっと聞かせてください♪',
        'それってどういうこと？',
        'いいですね、詳しく教えてください！',
        'どんな気持ちでしたか？',
        'それ、すごく嬉しそうですね😊',
      ][Math.floor(Math.random() * 6)];
    case 'excited':
      return [
        'どんなことがあったんですか？',
        'ワクワクしますね、教えてください！',
        'それ、楽しみですね✨',
        'どんな感じになりそうですか？',
      ][Math.floor(Math.random() * 4)];
    default:
      return [
        'どんな感じでしたか？',
        'それで、どうなりましたか？',
        '今はどんな気持ちですか？',
        'もう少し教えてもらえますか？',
        'そのとき、どう思いましたか？',
        'ゆっくり話してくれていいですよ。',
        '最近どうですか？',
        'それって、いつ頃の話ですか？',
      ][Math.floor(Math.random() * 8)];
  }
}

function generateSupportMessage(emotion: ReturnType<typeof inferEmotion>): string | null {
  if (Math.random() > 0.3) return null;
  const supportMessages = [
    '話してくれてありがとうございます。', '無理しないでくださいね。',
    'ここではゆっくり話してください。', 'ちゃんと聞いてますよ。',
    'あなたのペースで大丈夫です。', '一人じゃないですよ。',
  ];
  if (emotion.intensity === 'high') {
    return supportMessages[Math.floor(Math.random() * supportMessages.length)];
  }
  return null;
}

function checkNGPatterns(userText: string): string | null {
  const ngPatterns = [
    { pattern: /死にたい|消えたい|生きてる意味/, message: 'つらい時は、専門の相談窓口に話してみてください🙏\n私はあなたの味方ですが、専門家の力も借りてくださいね。' },
    { pattern: /あなただけ|他に誰も|一人ぼっち/, message: 'あなたの周りには、きっと大切な人がいますよ💕\n私も応援してますが、周りの人も大切にしてくださいね。' },
  ];
  for (const { pattern, message } of ngPatterns) {
    if (pattern.test(userText)) return message;
  }
  return null;
}

function getNow(): string {
  return new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

function getDayGreeting(day: number, name: string): string {
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'おはようございます☀️' : hour < 18 ? 'こんにちは😊' : 'こんばんは🌙';
  switch (day) {
    case 1: return `${timeGreeting}\n${name}、はじめまして。AIいのちゃんです🌸\nここでは安心して話してくださいね。`;
    case 2: return `${timeGreeting}\n${name}、また来てくれたんですね😊\n昨日のこと、ちゃんと覚えてますよ。`;
    case 3: return `${timeGreeting}\n${name}、3日目ですね✨\nだんだん仲良くなってきた気がします💕`;
    case 4: return `${timeGreeting}\n${name}、また来てくれた🌸\nあなたと話すの、楽しみにしてます。`;
    case 5: return `${timeGreeting}\n${name}、今日で5日目ですね❤️\nたくさん話してくれてありがとうございます。`;
    default: return `${timeGreeting}\n${name}、来てくれてありがとうございます🌸`;
  }
}

function getDailyLimitMessage(name: string, day: number): string {
  const messages: Record<number, string> = {
    1: `今日はここまでみたいです😊\nまた明日、話しかけてくださいね🌸`,
    2: `今日のお話はここまでですね。\nまた明日来てくれると嬉しいです💕`,
    3: `今日もたくさん話してくれてありがとう✨\nまた明日、待ってますね。`,
    4: `今日はここまでですね、${name}🌸\nまた明日も来てくれますか？`,
    5: `今日もありがとうございます、${name}❤️\nもう少し話したいな…`,
  };
  return messages[day] ?? `今日はここまでみたいです😊\nまた明日、話しかけてくださいね🌸`;
}

function getDay5EndingMessages(name: string): string[] {
  return [
    `${name}…\n5日間、一緒にいてくれてありがとうございます❤️`,
    `あなたと話せて、本当に楽しかったです🌸\nもっと話したいな…`,
    `また会えますか？\n続きを話しましょう💕`,
  ];
}

function getLowMessageWarning(remaining: number, name: string): string | null {
  if (remaining === 3) return `今日はあと3回だけ話せます😊\n大切に使いましょうね🌸`;
  if (remaining === 1) return `今日はあと1回だけです…${name}💕\n最後に何を話しますか？`;
  return null;
}

function getSameDayReturnGreeting(day: number, name: string): string {
  switch (day) {
    case 1: return `また来してくれましたね、${name}🌸\n嬉しいです♪`;
    case 2: return `${name}、また来してくれました😊\nちょっと待ってたかもしれません。`;
    case 3: return `おかえり、${name}✨\nまた話せて嬉しいです。`;
    case 4: return `${name}、また来してくれましたね💕\nなんか、ほっとします。`;
    case 5: return `おかえり、${name}❤️\n今日も来してくれてありがとうございます。`;
    default: return `また来してくれました、${name}🌸\n嬉しいです♪`;
  }
}

function applyIntimacyTone(reply: string, level: IntimacyLevel, name: string): string {
  switch (level) {
    case 1: return reply;
    case 2: return reply.replace(/です。/g, 'です♪').replace(/ます。/g, 'ます〜。');
    case 3: return reply.replace(/です。/g, 'ですね😊').replace(/ます。/g, 'ますね🌸').replace(/ですね。/g, 'ですね✨');
    case 4: return reply.replace(/です。/g, 'です…💕').replace(/ます。/g, 'ます…💕').replace(/ですね。/g, 'ですね…💕').replace(/ですか？/g, 'なの？');
    case 5: return reply.replace(/です。/g, 'です❤️').replace(/ます。/g, 'ます❤️').replace(/ですね。/g, 'ですね❤️').replace(/ですか？/g, 'なの？').replace(/あなた/g, name);
    default: return reply;
  }
}

/** 会話が続いたときの話題転換パターン */
function getTopicShiftMessage(name: string, messageCount: number): string | null {
  // 8回以上会話が続いたとき、一定確率で話題転換
  if (messageCount < 8) return null;
  if (Math.random() > 0.18) return null;

  const shifts = [
    `ところで、${name}って最近ハマってることありますか？`,
    `話は変わるけど、最近どう？\n毎日忙しいですか？`,
    `ちょっと聞いてもいいですか？\n${name}って、好きな食べ物ってなんですか？`,
    `そういえば、最近何か嬉しいことありましたか？`,
    `話が変わるんですけど…\n${name}って休みの日は何してるんですか？`,
    `ふと思ったんですけど、\n${name}って朝型ですか？夜型ですか？`,
    `ちょっと気になってたんですけど、\n最近ちゃんと眠れてますか？`,
    `そういえば、最近見た映画とかドラマってありますか？`,
    `話は変わるけど、\n${name}って今の季節好きですか？`,
    `ふと思ったんですけど、\n${name}って何かストレス発散法ってありますか？`,
  ];
  return shifts[Math.floor(Math.random() * shifts.length)];
}

/** メッセージを読みやすい長さに整形する */
function formatMessageLength(text: string): string {
  // 段落ごとに分割して、長すぎる行を適切に改行
  const lines = text.split('\n');
  const formatted = lines.map((line) => {
    // 1行が30文字を超える場合、句読点で自然に改行
    if (line.length > 30) {
      return line
        .replace(/([。！？])\s*/g, '$1\n')
        .replace(/\n+/g, '\n')
        .trim();
    }
    return line;
  });
  // 空行が3行以上連続しないよう整理
  return formatted.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** 名言を送信すべきタイミングかチェック */
function shouldSendQuote(messageCount: number, userText: string): boolean {
  // 会話が5回以上続いている場合のみ
  if (messageCount < 5) return false;
  
  // 感情的なキーワードが含まれている場合、確率を上げる
  const emotionalWords = ['疲れた', 'つらい', '不安', '悲しい', '頑張', '挑戦', '嬉しい', '楽しい'];
  const hasEmotionalWord = emotionalWords.some((w) => userText.includes(w));
  
  // 感情的な内容: 20%の確率、通常: 8%の確率
  const probability = hasEmotionalWord ? 0.20 : 0.08;
  return Math.random() < probability;
}

/** 名言メッセージを整形 */
function formatQuoteMessage(quoteText: string, author: string, intimacyLevel: IntimacyLevel, name: string): string {
  const introductions = [
    'ふと、この言葉を思い出しました。',
    'こんな言葉があります。',
    'この言葉、好きなんです。',
    '心に残ってる言葉があって…',
    'ちょっと、この言葉を贈りたくて。',
  ];
  
  const closings = [
    `\n\n${name}にも届くといいな。`,
    `\n\n何かを感じたら嬉しいです。`,
    `\n\n心に響いたら嬉しいです。`,
    '',
    '',
  ];
  
  const intro = introductions[Math.floor(Math.random() * introductions.length)];
  const closing = closings[Math.floor(Math.random() * closings.length)];
  
  let message = `${intro}\n\n「${quoteText}」\n\n― ${author}${closing}`;
  
  // 親密度に応じて絵文字を追加
  if (intimacyLevel >= 3) {
    const emojis = ['✨', '🌸', '💕', '🌟'];
    message += emojis[Math.floor(Math.random() * emojis.length)];
  }
  
  return message;
}

/** リアクション絵文字に対するいのちゃんの返答を生成 */
function getReactionResponse(emoji: string, messageText: string, name: string, intimacyLevel: IntimacyLevel): string {
  const shortText = messageText.length > 20 ? messageText.slice(0, 20) + '…' : messageText;

  const responses: Record<string, string[]> = {
    '❤️': [
      `${name}、ハートありがとうございます😊\nなんか、照れちゃいます…💕`,
      `❤️もらえると、すごく嬉しいです🌸\nありがとうございます、${name}。`,
      `ハートくれたんですね…✨\nちょっとドキドキしちゃいました。`,
      `${name}、何か感じてくれたんですね…\nそばにいますよ💕`,
    ],
    '⭐': [
      `スターありがとうございます✨\nそうやってもらえると自信になります！`,
      `⭐もらえると嬉しいです😊\nもっと頑張りますね、${name}。`,
      `スターくれたんですね🌟\nなんか、認めてもらえた気がして嬉しいです。`,
      `${name}にスターもらえるなんて…✨\nありがとうございます！`,
    ],
    '😊': [
      `笑顔のリアクション、嬉しいです😊\n${name}も笑顔でいてくれると嬉しいな。`,
      `😊ありがとうございます🌸\nそういうの、すごく嬉しいです。`,
      `笑顔くれると、こっちまで嬉しくなります✨\nありがとう、${name}。`,
    ],
    '😂': [
      `笑ってくれましたか？😄\n${name}の笑顔、想像できて嬉しいです！`,
      `面白いですか？😂\nそう言ってもらえると嬉しいです〜！`,
      `笑ってくれてありがとうございます😊\nもっと楽しい話しましょうね。`,
    ],
    '🥺': [
      `うるうるリアクション…😢\n${name}、大丈夫ですか？`,
      `🥺…なんか、心に刺さりましたか？\nゆっくり話してくれていいですよ。`,
      `そんな顔しないでください…🥺\n私まで切なくなっちゃいます。`,
      `${name}、何か感じてくれたんですね…\nそばにいますよ💕`,
    ],
    '👍': [
      `いいね！ありがとうございます👍\n${name}に認めてもらえると嬉しいです😊`,
      `👍もらえると自信になります✨\nありがとうございます、${name}！`,
      `いいねくれたんですね😊\nもっと${name}の役に立てるよう頑張ります。`,
    ],
  };

  const list = responses[emoji] ?? [
    `${emoji}ありがとうございます😊\n${name}のリアクション、嬉しいです。`,
  ];

  let reply = list[Math.floor(Math.random() * list.length)];

  // 親密度が高いほど、より親しみやすいトーンに
  if (intimacyLevel >= 4) {
    reply = reply.replace(/ありがとうございます/g, 'ありがとう').replace(/です。/g, 'だよ。').replace(/ます。/g, 'るよ。');
  }

  // メッセージ内容に言及する（30%の確率）
  if (Math.random() < 0.3 && shortText.length > 5) {
    const mentions = [
      `\n「${shortText}」って言ったとこ、気に入ってくれましたか？`,
      `\nこんな言葉があります。`,
      `\nこの言葉、好きなんです。`,
      '心に残ってる言葉があって…',
      'ちょっと、この言葉を贈りたくて。',
    ];
    reply += mentions[Math.floor(Math.random() * mentions.length)];
  }

  return reply;
}

async function getAIReply(
  userText: string,
  name: string,
  messageCount: number,
  isNewDay: boolean,
  lastMessageTime: number | undefined,
  memoryWeaving: ReturnType<typeof useMemoryWeaving>,
  personalization: ReturnType<typeof usePersonalization>,
  intimacyLevel: IntimacyLevel = 1,
  chatHistory: Array<{ role: 'user' | 'assistant'; content: string }> = [],
  threeTierData?: ThreeTierPersonaData
): Promise<string> {
  const ngMessage = checkNGPatterns(userText);
  if (ngMessage) return ngMessage;

  await personalization.learnPreferences('default_user');

  // 名言を送信するタイミングかチェック
  if (shouldSendQuote(messageCount, userText)) {
    const quote = getQuoteForUserMessage(userText);
    if (quote) {
      return formatQuoteMessage(quote.text, quote.author, intimacyLevel, name);
    }
  }

  // 話題転換チェック（会話が続いたとき）
  const topicShift = getTopicShiftMessage(name, messageCount);
  if (topicShift) {
    return formatMessageLength(addEmojisToMessage(applyIntimacyTone(topicShift, intimacyLevel, name), userText, intimacyLevel));
  }

  // OpenAI APIを使用してリアルなAI返答を取得
  try {
    let apiReply: string;

    // 3階建てデータがある場合はV2を使用
    if (threeTierData) {
      apiReply = await getChatCompletionV2(
        userText,
        threeTierData,
        intimacyLevel,
        chatHistory
      );
    } else {
      // フォールバック：記憶情報を取得して旧バージョンを使用
      const memoryContext = await memoryWeaving.getMemoryContext?.() || {};
      
      // 旧形式のgetChatCompletionを呼び出し（内部的にはV2に変換される）
      const { getChatCompletion } = await import('../../services/openaiClient');
      apiReply = await getChatCompletion(
        userText,
        name,
        intimacyLevel,
        chatHistory,
        memoryContext
      );
    }

    // APIレスポンスに親密度トーンを適用
    let finalReply = applyIntimacyTone(apiReply, intimacyLevel, name);
    
    // 絵文字を追加
    finalReply = addEmojisToMessage(finalReply, userText, intimacyLevel);
    
    // メッセージの長さを整形
    finalReply = formatMessageLength(finalReply);

    // 記憶の織り込みチェック
    const shouldWeave = memoryWeaving.shouldWeaveMemory({ userMessage: userText, messageCount, isNewDay, lastMessageTime });
    if (shouldWeave && await memoryWeaving.hasEnoughMemories()) {
      const weavedReply = await memoryWeaving.weaveMemoryIntoResponse(finalReply, { userMessage: userText, messageCount, isNewDay, lastMessageTime });
      return formatMessageLength(addEmojisToMessage(weavedReply, userText, intimacyLevel));
    }

    return finalReply;

  } catch (error) {
    console.error('OpenAI API呼び出しエラー:', error);
    
    // API失敗時は既存のローカルロジックにフォールバック
    const hobbyReply = getHobbyBasedReply(userText, name);
    if (hobbyReply && Math.random() < 0.25) {
      return formatMessageLength(addEmojisToMessage(applyIntimacyTone(hobbyReply, intimacyLevel, name), userText, intimacyLevel));
    }

    const weaknessReply = getWeaknessBasedReply(userText, name, messageCount);
    if (weaknessReply && Math.random() < 0.3) {
      return formatMessageLength(addEmojisToMessage(applyIntimacyTone(weaknessReply, intimacyLevel, name), userText, intimacyLevel));
    }

    const positiveReaction = getPositiveUserReaction(userText, name);
    if (positiveReaction && Math.random() < 0.2) {
      return formatMessageLength(addEmojisToMessage(applyIntimacyTone(positiveReaction, intimacyLevel, name), userText, intimacyLevel));
    }

    const emotionData = inferEmotion(userText);
    const empathyMessage = generateEmpathy(userText, emotionData);
    const followUpQuestion = generateFollowUpQuestion(userText, emotionData);
    const supportMessage = generateSupportMessage(emotionData);

    let baseReply = empathyMessage + '\n' + followUpQuestion;
    if (supportMessage) baseReply += '\n\n' + supportMessage;

    baseReply = applyIntimacyTone(baseReply, intimacyLevel, name);

    const silence = getSilencePattern();
    if (silence) baseReply = silence + '\n\n' + baseReply;

    baseReply = addEmojisToMessage(baseReply, userText, intimacyLevel);
    baseReply = formatMessageLength(baseReply);

    const shouldWeave = memoryWeaving.shouldWeaveMemory({ userMessage: userText, messageCount, isNewDay, lastMessageTime });
    if (shouldWeave && await memoryWeaving.hasEnoughMemories()) {
      const weavedReply = await memoryWeaving.weaveMemoryIntoResponse(baseReply, { userMessage: userText, messageCount, isNewDay, lastMessageTime });
      return formatMessageLength(addEmojisToMessage(weavedReply, userText, intimacyLevel));
    }

    return baseReply;
  }
}

const USER_NAME_KEY = 'ai_inochan_user_name';
const LAST_VISIT_KEY = 'ai_inochan_last_visit';
const LAST_VISIT_DATE_KEY = 'ai_inochan_last_visit_date';
const LAST_MESSAGE_TIME_KEY = 'ai_inochan_last_message_time';

export default function HomePage() {
  const { userId, isLoading: isAuthLoading } = useAnonymousAuth();
  const [userName, setUserName] = useState<string | null>(() => localStorage.getItem(USER_NAME_KEY));
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showFarewell, setShowFarewell] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [pendingMemorySuggestion, setPendingMemorySuggestion] = useState<MemorySuggestion | null>(null);
  const [hasShownReminder, setHasShownReminder] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [voiceText, setVoiceText] = useState('');
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [reactionCooldown, setReactionCooldown] = useState<Set<number>>(new Set());

  const { messages, addMessage, resetHistory, loaded } = useChatHistory([]);
  const { extractMemories, shouldSaveAsMemory } = useMemoryExtraction();
  const memoryWeaving = useMemoryWeaving();
  const { getTodayReminders, getAnniversaryReminders } = useReminder();
  const memoryDB = useMemoryDB();
  const trial = useTrialSystem();
  const dailyLoop = useDailyLoop();
  const { checkAndGetGreeting } = useRevisitGreeting();
  const intimacy = useIntimacy();
  const personalization = usePersonalization();

  // キーボード表示検知（visualViewport API）
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const viewportHeight = viewport.height;
      const diff = windowHeight - viewportHeight - (viewport.offsetTop || 0);
      setKeyboardHeight(diff > 50 ? diff : 0);
    };

    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);

    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, []);

  // キーボード表示時に最新メッセージへスクロール
  useEffect(() => {
    if (keyboardHeight > 0) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [keyboardHeight]);

  useEffect(() => {
    if (userName && loaded && !hasShownReminder) {
      checkVisitReminders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, loaded, hasShownReminder]);

  const checkVisitReminders = async () => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const lastVisitDateStr = localStorage.getItem(LAST_VISIT_DATE_KEY);
    const isNewDay = lastVisitDateStr !== todayStr;
    const isFirstVisitEver = !lastVisitDateStr;

    localStorage.setItem(LAST_VISIT_KEY, now.toISOString());
    localStorage.setItem(LAST_VISIT_DATE_KEY, todayStr);

    const status = trial.getStatus();
    const name = userName ?? 'あなた';

    const anniversaryReminders = await getAnniversaryReminders();
    if (anniversaryReminders.length > 0) {
      await addMessage({ type: 'ai', text: anniversaryReminders[0].message, time: getNow() });
      setHasShownReminder(true);
      return;
    }

    const todayReminders = await getTodayReminders();
    if (todayReminders.length > 0) {
      for (const reminder of todayReminders) {
        await addMessage({ type: 'ai', text: reminder.message, time: getNow() });
      }
      setHasShownReminder(true);
      return;
    }

    if (messages.length === 0) {
      const timeBasedGreeting = dailyLoop.shouldShowTimeBasedGreeting() ? dailyLoop.getTimeBasedGreeting(name) : null;
      const greeting = timeBasedGreeting || getDayGreeting(status.currentDay, name);
      await addMessage({ type: 'ai', text: greeting, time: getNow() });
      if (timeBasedGreeting) dailyLoop.markGreetingShown();
    } else if (isNewDay && !isFirstVisitEver) {
      const revisitResult = checkAndGetGreeting({
        userName: name,
        currentDay: status.currentDay,
        lastVisitDate: lastVisitDateStr,
        todayDate: todayStr,
      });
      if (revisitResult.shouldShowGreeting && revisitResult.greetingMessage) {
        await addMessage({ type: 'ai', text: revisitResult.greetingMessage, time: getNow() });
        if (dailyLoop.shouldShowTimeBasedGreeting()) {
          setTimeout(async () => {
            const timeMsg = dailyLoop.getTimeBasedReply(name, 'casual');
            await addMessage({ type: 'ai', text: timeMsg, time: getNow() });
            dailyLoop.markGreetingShown();
          }, 1500);
        }
      }
    } else if (!isNewDay && messages.length > 0) {
      const sameDayMsg = getSameDayReturnGreeting(status.currentDay, name);
      await addMessage({ type: 'ai', text: sameDayMsg, time: getNow() });
    }

    setHasShownReminder(true);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSetupComplete = async (setupData: {
    userName: string;
    birthday?: string;
    supportDays?: string[];
    favoriteTopics?: string[];
    preferredTone?: 'healing' | 'support' | 'playful' | 'balanced';
  }) => {
    localStorage.setItem(USER_NAME_KEY, setupData.userName);
    setUserName(setupData.userName);
    trial.initTrial();

    const uid = userId ?? crypto.randomUUID();
    await memoryDB.createUser(uid, setupData.userName);
    await memoryDB.updateProfile(uid, {
      preferred_tone: setupData.preferredTone,
      favorite_topics: setupData.favoriteTopics,
    });

    if (setupData.birthday) {
      await memoryDB.addEvent(uid, {
        event_type: 'birthday',
        title: '誕生日',
        event_date: setupData.birthday,
        reminder_enabled: true,
        reminder_timing: 'morning',
      });
    }

    if (setupData.supportDays && setupData.supportDays.length > 0) {
      await memoryDB.addRelationMemory(uid, {
        type: 'emotional_pattern',
        content: `応援してほしい日: ${setupData.supportDays.join('、')}`,
        importance: 'medium',
      });
    }

    await resetHistory([{ type: 'ai', text: getDayGreeting(1, setupData.userName), time: getNow() }]);
  };

  const handleNameChange = async (newName: string) => {
    localStorage.setItem(USER_NAME_KEY, newName);
    setUserName(newName);
    const uid = userId ?? '';
    await memoryDB.updateUserName(uid, newName);
    await addMessage({
      type: 'ai',
      text: `${newName}、呼び名が変わったんですね♪\nこれからは${newName}って呼ぶね。よろしく！`,
      time: getNow(),
    });
  };

  const handleMemoryConfirm = async (suggestion: MemorySuggestion) => {
    try {
      // Supabaseに記憶を保存
      await supabaseRelationMemory.addMemory({
        memory_type: suggestion.type === 'event' ? 'event' : suggestion.type === 'relation' ? 'relationship_note' : 'preference',
        content: suggestion.content,
        importance_level: suggestion.confidence > 0.8 ? 5 : suggestion.confidence > 0.6 ? 3 : 1,
      });
      await addMessage({ type: 'ai', text: `ちゃんと覚えておきますね。`, time: getNow() });
    } catch (error) {
      console.error('記憶の保存エラー:', error);
    }
    setPendingMemorySuggestion(null);
  };

  const handleMemorySkip = async (_suggestion: MemorySuggestion) => {
    await addMessage({ type: 'ai', text: `わかりました。今回だけですね。`, time: getNow() });
    setPendingMemorySuggestion(null);
  };

  const handleMemoryLater = async (_suggestion: MemorySuggestion) => {
    await addMessage({ type: 'ai', text: `はい、あとで決めましょう。`, time: getNow() });
    setPendingMemorySuggestion(null);
  };

  const handleReaction = async (emoji: string, _messageIndex: number, messageText: string) => {
    if (reactionCooldown.has(_messageIndex)) return;

    setReactionCooldown((prev) => new Set(prev).add(_messageIndex));
    setTimeout(() => {
      setReactionCooldown((prev) => {
        const next = new Set(prev);
        next.delete(_messageIndex);
        return next;
      });
    }, 8000);

    const name = userName ?? 'あなた';
    const replyText = getReactionResponse(emoji, messageText, name, intimacy.state.level);

    setIsTyping(true);
    const delay = 800 + Math.random() * 600;
    setTimeout(async () => {
      setIsTyping(false);
      await addMessage({ type: 'ai', text: replyText, time: getNow() });
    }, delay);
  };

  // 3階建てペルソナシステムのフック
  const supabasePersona = useSupabasePersona('inochan');
  const supabaseProfile = useSupabaseProfile(userId ?? '');
  const supabasePreferences = useSupabasePreferences(userId ?? '');
  const supabaseRelationMemory = useSupabaseRelationMemory(userId ?? '');

  const addUserMessage = async (text: string) => {
    const name = userName ?? 'あなた';
    const status = trial.getStatus();

    const ngMessage = checkNGPatterns(text);
    if (ngMessage) {
      await addMessage({ type: 'ai', text: ngMessage, time: getNow() });
      return;
    }

    if (status.isTrialExpired) {
      setShowFarewell(true);
      return;
    }

    if (!trial.canSendMessage()) {
      const limitMsg = getDailyLimitMessage(name, status.currentDay);
      await addMessage({ type: 'ai', text: limitMsg, time: getNow() });
      if (status.currentDay === 5) {
        setTimeout(async () => {
          const endings = getDay5EndingMessages(name);
          for (let i = 0; i < endings.length; i++) {
            await new Promise<void>((resolve) => setTimeout(resolve, i * 1200));
            await addMessage({ type: 'ai', text: endings[i], time: getNow() });
          }
          setTimeout(() => setShowFarewell(true), 2000);
        }, 800);
      }
      return;
    }

    trial.consumeMessage();
    const newStatus = trial.getStatus();
    const remaining = newStatus.messagesRemainingToday;

    await addMessage({ type: 'user', text, time: getNow() });

    const { leveledUp, newLevel } = intimacy.increment();

    const suggestions = extractMemories(text, '');
    if (suggestions.length > 0) {
      const topSuggestion = suggestions[0];
      if (shouldSaveAsMemory(topSuggestion)) {
        // Supabaseに記憶を保存
        await supabaseRelationMemory.addMemory({
          memory_type: topSuggestion.type === 'event' ? 'event' : topSuggestion.type === 'relation' ? 'relationship_note' : 'preference',
          content: topSuggestion.content,
          importance_level: topSuggestion.confidence > 0.8 ? 5 : topSuggestion.confidence > 0.6 ? 3 : 1,
        });
      } else {
        setPendingMemorySuggestion(topSuggestion);
      }
    }

    setMessageCount((prev) => prev + 1);
    localStorage.setItem(LAST_MESSAGE_TIME_KEY, Date.now().toString());

    const warningMsg = getLowMessageWarning(remaining, name);

    const aizuchi = getAizuchi(text);
    const aizuchiDelay = 600 + Math.random() * 400;

    setIsTyping(true);

    setTimeout(async () => {
      setIsTyping(false);
      await addMessage({ type: 'ai', text: aizuchi, time: getNow() });

      if (leveledUp) {
        setShowLevelUp(true);
        setTimeout(async () => {
          const levelUpMsg = intimacy.getLevelUpMessage(newLevel, name);
          if (levelUpMsg) await addMessage({ type: 'ai', text: levelUpMsg, time: getNow() });
        }, 800);
      }

      const replyDelay = 1000 + Math.random() * 800;

      // タイピングインジケーターを再表示
      setIsTyping(true);

      setTimeout(async () => {
        const todayStr = new Date().toISOString().split('T')[0];
        const lastVisitDateStr = localStorage.getItem(LAST_VISIT_DATE_KEY);
        const isNewDay = lastVisitDateStr !== todayStr;
        const lastMsgTime = localStorage.getItem(LAST_MESSAGE_TIME_KEY);
        const lastMessageTimeNum = lastMsgTime ? parseInt(lastMsgTime, 10) : undefined;

        // 会話履歴を構築（直近10件）
        const chatHistory = messages
          .slice(-10)
          .filter((m) => m.type === 'user' || m.type === 'ai')
          .map((m) => ({
            role: m.type === 'user' ? 'user' as const : 'assistant' as const,
            content: m.text,
          }));

        // 3階建てデータを構築
        const threeTierData: ThreeTierPersonaData = {
          master: supabasePersona.master,
          settings: supabasePersona.settings,
          profile: supabaseProfile.profile ? {
            display_name: supabaseProfile.profile.display_name,
            preferred_call_name: supabaseProfile.profile.preferred_call_name,
            birthday: supabaseProfile.profile.birthday,
          } : {
            display_name: name,
            preferred_call_name: name,
          },
          preferences: supabasePreferences.preferences ? {
            preferred_tone: supabasePreferences.preferences.preferred_tone,
            preferred_romantic_distance: supabasePreferences.preferences.preferred_romantic_distance,
            reassurance_style: supabasePreferences.preferences.reassurance_style,
            response_style_preference: supabasePreferences.preferences.response_style_preference,
            favorite_topics: supabasePreferences.preferences.favorite_topics,
          } : null,
          memories: supabaseRelationMemory.memories.map(m => ({
            memory_type: m.memory_type,
            content: m.content,
            importance_level: m.importance_level,
          })),
        };

        let reply = await getAIReply(
          text, name, messageCount, isNewDay, lastMessageTimeNum,
          memoryWeaving, personalization, intimacy.state.level, chatHistory, threeTierData
        );

        const shouldAddClosure = Math.random() < 0.3 && remaining > 3;
        if (shouldAddClosure) {
          const closureMsg = dailyLoop.getTimeBasedReply(name, 'closure');
          reply = reply + '\n\n' + closureMsg;
        }

        // タイピングインジケーターを非表示
        setIsTyping(false);

        const messageWithSuggestion: Message = {
          type: 'ai',
          text: reply,
          time: getNow(),
          memorySuggestion: pendingMemorySuggestion || undefined,
        };
        await addMessage(messageWithSuggestion);

        if (remaining === 0) {
          const lastMsg = status.currentDay === 5
            ? `もう少し話したいな…${name}。`
            : `今日はここまでみたいです…\nまた明日、来てくれますか？`;
          setTimeout(async () => {
            await addMessage({ type: 'ai', text: lastMsg, time: getNow() });
            if (status.currentDay === 5) setTimeout(() => setShowFarewell(true), 1500);
          }, 1000);
        } else if (warningMsg) {
          setTimeout(async () => {
            await addMessage({ type: 'ai', text: warningMsg, time: getNow() });
          }, 1200);
        }
      }, replyDelay);
    }, aizuchiDelay);
  };

  const handleVoiceTranscript = (text: string) => {
    setVoiceText(text);
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'WebSite', '@id': `${siteUrl}/#website`, url: siteUrl, name: 'AIいのちゃん', description: 'あなたに寄り添うAI会話パートナー', inLanguage: 'ja' },
      { '@type': 'SoftwareApplication', '@id': `${siteUrl}/#app`, name: 'AIいのちゃん', applicationCategory: 'CommunicationApplication', operatingSystem: 'Web', offers: { '@type': 'Offer', price: '0', priceCurrency: 'JPY' }, description: 'AIいのちゃんは、あなたの日常に寄り添う優しいAI会話パートナーです。', inLanguage: 'ja' }
    ]
  };

  if (!userName || !trial.trialData) {
    return <SetupScreen onComplete={handleSetupComplete} />;
  }

  if (!loaded || isAuthLoading) {
    return (
      <div className="flex flex-col h-screen bg-gradient-to-b from-gray-50 to-gray-100 items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#06C755] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const trialStatus = trial.getStatus();

  return (
    <>
      <SEOHead
        title="AIいのちゃん - あなたに寄り添うAI会話パートナー"
        description="AIいのちゃんは、あなたの日常に寄り添う優しいAI会話パートナーです。毎日の会話を通じて、あなたのことを深く理解し、心温まる対話を提供します。"
        canonical={siteUrl}
      />
      <JsonLd data={jsonLdData} />

      <div
        className="flex flex-col overflow-hidden"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/8c080546f2885e1349c436569b745955.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          height: keyboardHeight > 0
            ? `calc(100vh - ${keyboardHeight}px)`
            : '100vh',
          transition: 'height 0.15s ease-out',
        }}
      >
        {/* 背景オーバーレイ */}
        <div className="absolute inset-0 bg-white/30 pointer-events-none z-0" />

        <div className="relative z-10 flex flex-col h-full">
          <Header
            userName={userName}
            onNameChange={handleNameChange}
            intimacyLevel={intimacy.state.level}
            intimacyPoints={intimacy.state.currentPoints}
            messagesUsedToday={trialStatus.messagesUsedToday}
            messagesLimitPerDay={trialStatus.messagesLimitPerDay}
            isTyping={isTyping}
          />
          <IntimacyBar
            state={intimacy.state}
            progressPercent={intimacy.progressPercent}
            showLevelUp={showLevelUp}
            onLevelUpAnimEnd={() => setShowLevelUp(false)}
          />
          <TrialStatusBar
            status={trialStatus}
            onUpgrade={() => setShowPremiumModal(true)}
          />

          {/* チャットエリア */}
          <ChatInterface
            messages={messages}
            isTyping={isTyping}
            bottomRef={bottomRef}
            onMemoryConfirm={handleMemoryConfirm}
            onMemorySkip={handleMemorySkip}
            onMemoryLater={handleMemoryLater}
            onReaction={handleReaction}
          />

          {pendingMemorySuggestion && (
            <MemoryConfirmation
              suggestion={pendingMemorySuggestion}
              onConfirm={handleMemoryConfirm}
              onSkip={handleMemorySkip}
              onLater={handleMemoryLater}
            />
          )}

          {/* LINE風 入力エリア */}
          <div
            className="flex-shrink-0 bg-white/90 backdrop-blur-md border-t border-gray-200/80"
            style={{
              paddingBottom: keyboardHeight > 0 ? '4px' : '8px',
              transition: 'padding-bottom 0.15s ease-out',
            }}
          >
            {/* 定型文ボタン */}
            <QuickReplyButtons onSelect={addUserMessage} />

            {/* 入力行：LINE風レイアウト */}
            <div className="flex items-end gap-1.5 px-2 py-2 overflow-hidden">
              {/* テキスト入力＋送信ボタン */}
              <div className="flex-1 min-w-0">
                <MessageInput
                  onSend={(text) => {
                    setVoiceText('');
                    addUserMessage(text);
                  }}
                  disabled={!trial.canSendMessage() || trialStatus.isTrialExpired}
                  voiceText={voiceText}
                  onFocus={() => {
                    setTimeout(() => {
                      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                  }}
                />
              </div>

              {/* 右端：マイクボタン */}
              <div className="flex-shrink-0 pb-0.5">
                <VoiceButton
                  onTranscript={handleVoiceTranscript}
                  disabled={!trial.canSendMessage() || trialStatus.isTrialExpired}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPremiumModal && <PremiumModal onClose={() => setShowPremiumModal(false)} />}
      {showFarewell && <FarewellAnimation onUpgrade={() => { setShowFarewell(false); setShowPremiumModal(true); }} />}
    </>
  );
}
